import {Component} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatButtonModule} from '@angular/material/button';
import {TourTileComponent} from "./tour-tile/tour-tile.component";
import {MatIconModule} from "@angular/material/icon";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {DialpadDialogComponent} from "./dialpad-dialog/dialpad-dialog.component";
import {Observable, tap} from "rxjs";
import {Tour} from "./classes/tour.model";
import {TourService} from "./classes/tour.service";
import {LanguageService} from "./classes/language.service";
import {LanguageSelectPageComponent} from "./language-select-page/language-select-page.component";
import {Analytics, logEvent} from "@angular/fire/analytics";
import {Auth, GoogleAuthProvider, signInWithPopup, signOut, User, user} from "@angular/fire/auth";
import {RoleBasedDisplayDirective} from "./classes/role-based-display.directive";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule,
    RouterOutlet,
    MatToolbarModule,
    MatButtonModule,
    MatDialogModule,
    TourTileComponent, RouterLink, RouterLinkActive, MatIconModule, LanguageSelectPageComponent, RoleBasedDisplayDirective],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  title = 'audiotour';

  activeTour$: Observable<Tour> = this.tourService.activeTour$;
  currentStep: number = 0;

  user$: Observable<User | null>;
  changingLanguage: boolean = false;

  nextTourStep: Tour;

  constructor(
    private dialog: MatDialog,
    private tourService: TourService,
    protected languageService: LanguageService,
    private analytics: Analytics,
    private auth: Auth
  ) {
    this.activeTour$.subscribe(async (tour: Tour) => {
      if (!tour) {
        this.currentStep = null;
        return;
      }
      this.currentStep = tour.step;

      this.nextTourStep = await this.tourService.getTourByStepNumber(this.currentStep + 1, false).catch((err) => {
        return null
      });
    });

    logEvent(this.analytics, 'app_open',
      {
        "component": "AppComponent",
        "language": languageService.currentLang$.value,
        "first_visit": languageService.currentLang$.value === null
      });


    this.user$ = user(this.auth);

    this.user$.subscribe((user: User) => {
      console.log('New user', user);
      if (!user) {
        return;
      }
      user.getIdTokenResult().then((tokenResult) => {
        console.log('Token result', tokenResult);
        if (tokenResult.claims['admin']) {
          console.log('User is admin');
        }
      });
    });

    const latestTour = this.tourService.getLatestTour();
    console.log('Latest tour', latestTour);
  }

  async nextStep() {
    const lastTour = this.tourService.activeTour$.value;
    this.tourService.activeTour$.next(this.nextTourStep);

    logEvent(this.analytics, 'next_step', {
      "tour": "main_tour",
      "step_id": this.nextTourStep.id,
      "step": this.nextTourStep.step,
      "language": this.languageService.currentLang$.value,
      "last_step_id": lastTour?.id ?? "null",
      "last_step": lastTour?.step ?? "null",
    });
  }

  async previousStep() {
    const lastTour = this.tourService.activeTour$.value;
    const newTour = await this.tourService.getTourByStepNumber(this.currentStep - 1, true);

    logEvent(this.analytics, 'previous_step', {
      "tour": "main_tour",
      "step_id": newTour.id,
      "step": newTour.step,
      "language": this.languageService.currentLang$.value,
      "last_step_id": lastTour?.id ?? "null",
      "last_step": lastTour?.step ?? "null",
    });
  }

  openNumberDial() {
    this.dialog.open(DialpadDialogComponent, {});
  }

  loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    signInWithPopup(this.auth, provider)
      .then((result) => {
        // This gives you a Google Access Token. You can use it to access the Google API.
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential.accessToken;
        // The signed-in user info.
        const user = result.user;
        console.log('User from signinWithPopup', user);
        // IdP data available using getAdditionalUserInfo(result)
        // ...
      }).catch((error) => {
      // Handle Errors here.
      const errorCode = error.code;
      const errorMessage = error.message;
      // The email of the user's account used.
      const email = error.customData.email;
      // The AuthCredential type that was used.
      const credential = GoogleAuthProvider.credentialFromError(error);

      console.error('Error from signinWithPopup', error);
    });

  }

  logOut() {
    signOut(this.auth).then(() => {
      console.log('User signed out');
    });
  }

  changeLanguage(event: Event) {
    let value = (event.target as HTMLSelectElement).value;
    this.languageService.currentLang$.next(value);
  }
}
